import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { registerPushNotifications, unregisterPushToken } from '../services/PushService';
import { setDisplayTimeZone } from '../utils/dateFormatting';
import { hasPermission, isAdminUser } from '../lib/permissions';

const MobileAuthContext = createContext(null);
export { MobileAuthContext };

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session keys (token, operator, authType).
// Normal logins persist in localStorage so the session survives the app being closed
// ("stay logged in"). Super-admin impersonation stays in sessionStorage so it never
// outlives the tab.
const SESSION_KEYS = {
  token: '_mob_at',
  operator: '_mob_op',
  authType: '_mob_auth_type',
};

// Device-trust keys — localStorage (must persist across browser sessions)
const DEVICE_KEYS = {
  deviceToken: 'mobile_device_token',
  trustedUserInfo: 'mobile_trusted_user',
};

// Helpers
const getStored = (key, storage = sessionStorage) => storage.getItem(key);
const getStoredJSON = (key, storage = sessionStorage) => {
  try { const v = storage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
};

const isImpersonatingNow = () => sessionStorage.getItem('_mob_impersonating') === 'true';

// Read a session value from whichever store holds it
// (sessionStorage = active impersonation, localStorage = normal persisted login).
const readPersisted = (key) => {
  const v = sessionStorage.getItem(key);
  return v != null ? v : localStorage.getItem(key);
};
const readPersistedJSON = (key) => {
  try { const v = readPersisted(key); return v ? JSON.parse(v) : null; } catch { return null; }
};
// Write a session value to the correct store and clear any stale copy from the other.
const writeSession = (key, value) => {
  const imp = isImpersonatingNow();
  const target = imp ? sessionStorage : localStorage;
  const other = imp ? localStorage : sessionStorage;
  other.removeItem(key);
  if (value == null) target.removeItem(key);
  else target.setItem(key, value);
};
const clearAllSessionKeys = () => {
  Object.values(SESSION_KEYS).forEach((k) => {
    sessionStorage.removeItem(k);
    localStorage.removeItem(k);
  });
};

// Detect impersonation token from URL (super-admin → worker flow)
// Runs synchronously before MobileAuthProvider mounts so the initial token ref
// already carries the impersonation access_token.
const checkImpersonation = () => {
  const params = new URLSearchParams(window.location.search);
  const impersonateToken = params.get('impersonate');
  if (impersonateToken) {
    sessionStorage.setItem(SESSION_KEYS.token, impersonateToken);
    sessionStorage.setItem(SESSION_KEYS.authType, 'legacy');
    sessionStorage.setItem('_mob_impersonating', 'true');
    // Strip the token from the visible URL
    window.history.replaceState({}, document.title, window.location.pathname);
    return impersonateToken;
  }
  return readPersisted(SESSION_KEYS.token);
};

export const MobileAuthProvider = ({ children }) => {
  const tokenRef = useRef(checkImpersonation());
  const [isImpersonating, setIsImpersonating] = useState(
    () => sessionStorage.getItem('_mob_impersonating') === 'true'
  );
  const [operator, setOperator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(tokenRef.current);

  // V2 Auth state
  const [authType, setAuthType] = useState(readPersisted(SESSION_KEYS.authType) || 'legacy');
  const [deviceToken, setDeviceToken] = useState(getStored(DEVICE_KEYS.deviceToken, localStorage));
  const [trustedUserInfo, setTrustedUserInfo] = useState(
    () => getStoredJSON(DEVICE_KEYS.trustedUserInfo, localStorage)
  );
  const [v2AuthEnabled, setV2AuthEnabled] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [pendingPinSetup, setPendingPinSetup] = useState(false);
  const [pendingPasswordChange, setPendingPasswordChange] = useState(false);
  const [pendingOperatorData, setPendingOperatorData] = useState(null);
  const [tenantSettings, setTenantSettings] = useState({ time_format: '24h', date_format: 'DD/MM/YYYY' });
  const [platformFlags, setPlatformFlags] = useState({});
  const [platformFlagsLoaded, setPlatformFlagsLoaded] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const lastSettingsRefresh = useRef(0);

  // Token setter — keeps ref + state + persisted storage in sync
  const setToken = useCallback((newToken) => {
    tokenRef.current = newToken;
    setTokenState(newToken);
    writeSession(SESSION_KEYS.token, newToken || null);
  }, []);

  const updateOperator = useCallback((data) => {
    setOperator(data);
    writeSession(SESSION_KEYS.operator, data ? JSON.stringify(data) : null);
  }, []);

  // Axios instance — Bearer from ref + withCredentials for httpOnly cookies
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
    });
    instance.interceptors.request.use((config) => {
      const t = tokenRef.current;
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    return instance;
  }, []);

  // Register for native push notifications when operator is authenticated
  useEffect(() => {
    if (operator?.id && tokenRef.current) {
      registerPushNotifications(api);
    }
  }, [operator?.id, api]);

  // Refreshable tenant settings fetcher
  const refreshTenantSettings = useCallback(async () => {
    if (!operator?.client_id || !tokenRef.current) return;
    try {
      const res = await api.get(`/api/tenants/${operator.client_id}`);
      setTenantSettings({
        time_format: res.data?.time_format || '24h',
        date_format: res.data?.date_format || 'DD/MM/YYYY',
        field_rules: res.data?.field_rules || {},
        default_working_hours: res.data?.default_working_hours || { start: '07:00', end: '17:00' },
        depots_enabled: res.data?.depots_enabled ?? false,
        separate_operator_line: res.data?.separate_operator_line ?? false,
        docket_depot_times_enabled: res.data?.docket_depot_times_enabled ?? false,
        worker_job_email_enabled: res.data?.worker_job_email_enabled ?? false,
        xero_connected: res.data?.xero_connected ?? false,
        myob_connected: res.data?.myob_connected ?? false,
        qbo_connected: res.data?.qbo_connected ?? false,
        currency_symbol: res.data?.currency_symbol || '$',
        tax_rate: res.data?.tax_rate ?? 10,
        tax_label: res.data?.tax_label || 'GST',
      });
      if (res.data?.timezone) setDisplayTimeZone(res.data.timezone);
    } catch { /* network / auth error — keep stale settings */ }
  }, [operator?.client_id, api]);

  // Refreshable platform flags fetcher
  const refreshPlatformFlags = useCallback(async () => {
    if (!operator?.client_id || !tokenRef.current) return;
    try {
      const res = await api.get('/api/feature-flags/resolved');
      setPlatformFlags(res.data?.flags || {});
    } catch {
      /* keep existing flags on error */
    }
  }, [operator?.client_id, api]);

  // Fetch tenant settings when operator is authenticated
  useEffect(() => {
    if (operator?.client_id && tokenRef.current) {
      refreshTenantSettings();
    }
  }, [operator?.client_id, refreshTenantSettings]);

  // Fetch platform feature flags (super-admin managed, env/client scoped) when authed
  useEffect(() => {
    if (operator?.client_id && tokenRef.current) {
      setPlatformFlagsLoaded(false);
      refreshPlatformFlags().finally(() => setPlatformFlagsLoaded(true));
    } else {
      setPlatformFlags({});
      setPlatformFlagsLoaded(false);
    }
  }, [operator?.client_id, refreshPlatformFlags]);

  // Refresh settings + flags when the app returns to the foreground (throttled to 60s)
  useEffect(() => {
    if (!operator?.client_id) return;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastSettingsRefresh.current < 60_000) return;
      lastSettingsRefresh.current = now;
      refreshTenantSettings();
      refreshPlatformFlags();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [operator?.client_id, refreshTenantSettings, refreshPlatformFlags]);

  // Check if v2 auth is enabled
  useEffect(() => {
    const checkAuthFlags = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/feature-flags`);
        setV2AuthEnabled(response.data.new_auth_enabled || false);
      } catch {
        setV2AuthEnabled(false);
      }
    };
    checkAuthFlags();
  }, []);

  // Initialize from storage
  useEffect(() => {
    const impersonating = sessionStorage.getItem('_mob_impersonating') === 'true';
    const storedOperator = readPersistedJSON(SESSION_KEYS.operator);

    // Impersonation flow: token is set but no operator data yet — hydrate via whoami.
    if (impersonating && tokenRef.current && !storedOperator) {
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/api/mobile/auth/whoami`, {
            headers: { Authorization: `Bearer ${tokenRef.current}` },
            withCredentials: true,
          });
          if (res.data?.operator) {
            updateOperator(res.data.operator);
          }
          if (res.data?.subscription_status) setSubscriptionStatus(res.data.subscription_status);
        } catch (e) {
          // Token invalid or expired — clear so user is bumped to login
          sessionStorage.removeItem(SESSION_KEYS.token);
          sessionStorage.removeItem('_mob_impersonating');
          tokenRef.current = null;
          setTokenState(null);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    // Normal persisted session: hydrate instantly for no-flash UX, then validate and
    // slide the 30-day window in the background so active users stay logged in.
    if (tokenRef.current && !impersonating) {
      if (storedOperator) setOperator(storedOperator);
      (async () => {
        try {
          const res = await axios.post(
            `${API_URL}/api/mobile/auth/refresh`, {},
            { headers: { Authorization: `Bearer ${tokenRef.current}` }, withCredentials: true }
          );
          if (res.data?.access_token) setToken(res.data.access_token);
          if (res.data?.subscription_status) setSubscriptionStatus(res.data.subscription_status);
          if (!storedOperator) {
            const who = await axios.get(`${API_URL}/api/mobile/auth/whoami`, {
              headers: { Authorization: `Bearer ${tokenRef.current}` }, withCredentials: true,
            });
            if (who.data?.operator) updateOperator(who.data.operator);
            if (who.data?.subscription_status) setSubscriptionStatus(who.data.subscription_status);
          }
        } catch (err) {
          // Only a genuine auth rejection ends the session; network/offline errors
          // keep the user logged in with the stored session.
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            clearAllSessionKeys();
            tokenRef.current = null;
            setTokenState(null);
            setOperator(null);
          }
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    if (storedOperator && tokenRef.current) {
      setOperator(storedOperator);
    }
    setLoading(false);
  }, [updateOperator, setToken]);

  // Check if device token is still valid
  const checkDeviceToken = useCallback(async () => {
    if (!deviceToken) return { valid: false, reason: 'no_token' };

    try {
      const response = await axios.get(
        `${API_URL}/api/v2/mobile/auth/check-device?device_token=${deviceToken}`
      );

      if (response.data.valid) {
        const userInfo = {
          name: response.data.user_name,
          email: response.data.user_email,
          deviceName: response.data.device_name,
          trustedAt: response.data.trusted_at
        };
        setTrustedUserInfo(userInfo);
        localStorage.setItem(DEVICE_KEYS.trustedUserInfo, JSON.stringify(userInfo));
        return { valid: true, userInfo };
      }

      clearDeviceToken();
      return { valid: false, reason: response.data.reason };
    } catch (error) {
      console.error('Device check failed:', error);
      return { valid: false, reason: 'error' };
    }
  }, [deviceToken]);

  // Legacy login (Client ID + Operator Code + PIN)
  const login = async (clientId, operatorCode, pin) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/mobile/auth/login`,
        { client_id: clientId, operator_code: operatorCode, pin },
        { withCredentials: true }
      );

      const { access_token, operator: opData } = response.data;

      sessionStorage.removeItem('_mob_impersonating');
      setIsImpersonating(false);
      setToken(access_token);
      updateOperator(opData);
      writeSession(SESSION_KEYS.authType, 'legacy');
      setAuthType('legacy');
      setSubscriptionStatus(response.data.subscription_status || null);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  // V2 Email + Password login
  const loginV2 = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/v2/mobile/auth/login`, {
        email,
        password
      }, { withCredentials: true });

      const { access_token, user, tenants: userTenants, requires_tenant_selection, requires_pin_setup, requires_password_change } = response.data;

      sessionStorage.removeItem('_mob_impersonating');
      setIsImpersonating(false);
      setToken(access_token);
      writeSession(SESSION_KEYS.authType, 'v2');
      setAuthType('v2');
      setTenants(userTenants || []);
      setSubscriptionStatus(response.data.subscription_status || null);
      const opData = {
        id: user.id,
        name: user.full_name,
        email: user.email,
        client_id: user.active_tenant,
        tenant_features: user.tenant_features || [],
        mobile_quotes_enabled: user.mobile_quotes_enabled || false,
        roles: user.roles || [],
        role: user.role || 'viewer',
        is_worker: user.is_worker !== false, // default true for backward compat
        company_name: user.company_name,
        tenant_tier: user.tenant_tier,
        tenant_industry: user.tenant_industry,
        operator_code: user.operator_code,
        is_yard_dispatcher: user.is_yard_dispatcher || false,
        operator_id: user.operator_id || null,

      };

      if (requires_password_change) {
        setPendingOperatorData(opData);
        setPendingPasswordChange(true);
      } else if (requires_tenant_selection) {
        // Stash operator data but DO NOT activate it yet — activating `operator`
        // triggers the redirect-to-dashboard effect before the picker can render.
        // `selectTenant()` will activate the operator after the user picks a tenant.
        setPendingOperatorData(opData);
      } else {
        updateOperator(opData);
        setPendingPinSetup(requires_pin_setup);
      }

      return {
        success: true,
        requiresTenantSelection: requires_tenant_selection,
        requiresPinSetup: requires_pin_setup && !requires_password_change,
        requiresPasswordChange: requires_password_change,
        tenants: userTenants,
        user
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  // Change password (for must_change_password flow)
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${API_URL}/api/v2/mobile/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${tokenRef.current}` }, withCredentials: true }
      );

      setPendingPasswordChange(false);

      if (pendingOperatorData) {
        updateOperator(pendingOperatorData);
        setPendingOperatorData(null);
      }

      setPendingPinSetup(true);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to change password'
      };
    }
  };

  // Select tenant after multi-tenant login (mobile flow)
  const selectTenant = async (clientId) => {
    if (!tokenRef.current) {
      return { success: false, error: 'Not authenticated' };
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/v2/auth/select-tenant`,
        { client_id: clientId },
        { headers: { Authorization: `Bearer ${tokenRef.current}` }, withCredentials: true }
      );

      const { access_token, user } = response.data;
      setToken(access_token);
      setSubscriptionStatus(response.data.subscription_status || null);

      const opData = {
        id: user.id,
        name: user.full_name,
        email: user.email,
        client_id: user.active_tenant,
        company_name: user.company_name,
        role: user.role,
        roles: user.roles || [user.role],
        tenant_tier: user.tenant_tier,
        tenant_industry: user.tenant_industry,
        tenant_features: user.tenant_features || [],
        operator_code: user.operator_code,
        mobile_quotes_enabled: user.mobile_quotes_enabled === true,
        is_yard_dispatcher: user.is_yard_dispatcher === true,
        is_worker: user.is_worker !== false,
        operator_id: user.operator_id || null,
      };
      updateOperator(opData);
      setPendingOperatorData(null);

      // After selecting a tenant, PIN setup becomes available
      setPendingPinSetup(true);

      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to select tenant'
      };
    }
  };

  // V2 PIN login (for trusted devices)
  const loginWithPin = async (pin) => {
    if (!deviceToken) {
      return { success: false, error: 'No device token. Please log in with email.' };
    }

    try {
      const response = await axios.post(`${API_URL}/api/v2/mobile/auth/pin-login`, {
        device_token: deviceToken,
        pin
      }, { withCredentials: true });

      const { access_token, user } = response.data;

      sessionStorage.removeItem('_mob_impersonating');
      setIsImpersonating(false);
      setToken(access_token);
      writeSession(SESSION_KEYS.authType, 'v2');
      setAuthType('v2');

      const opData = {
        id: user.id,
        name: user.full_name,
        email: user.email,
        client_id: user.active_tenant,
        company_name: user.company_name,
        role: user.role,
        roles: user.roles || [user.role || 'viewer'],
        tenant_features: user.tenant_features || [],
        mobile_quotes_enabled: user.mobile_quotes_enabled || false,
        is_worker: user.is_worker !== false,
        is_yard_dispatcher: user.is_yard_dispatcher || false,
      };

      updateOperator(opData);

      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail || 'PIN login failed';
      return {
        success: false,
        error: detail,
        isLocked: detail.includes('Too many failed attempts'),
        deviceInvalid: detail.includes('Device not recognized')
      };
    }
  };

  // Setup PIN for device trust
  const setupPin = async (pin, deviceName = null, deviceType = 'mobile') => {
    if (!tokenRef.current) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/v2/mobile/auth/setup-pin`,
        { pin, device_name: deviceName, device_type: deviceType },
        { headers: { Authorization: `Bearer ${tokenRef.current}` }, withCredentials: true }
      );

      const { device_token: newDeviceToken } = response.data;

      // Device tokens stay in localStorage (device trust persists across sessions)
      localStorage.setItem(DEVICE_KEYS.deviceToken, newDeviceToken);
      setDeviceToken(newDeviceToken);

      const userInfo = {
        name: operator?.name,
        email: operator?.email,
        trustedAt: new Date().toISOString()
      };
      localStorage.setItem(DEVICE_KEYS.trustedUserInfo, JSON.stringify(userInfo));
      setTrustedUserInfo(userInfo);

      setPendingPinSetup(false);

      return { success: true, deviceToken: newDeviceToken };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to setup PIN'
      };
    }
  };

  // Switch account (clear device trust)
  const switchAccount = async () => {
    if (deviceToken) {
      try {
        await axios.post(`${API_URL}/api/v2/mobile/auth/switch-account`, {
          device_token: deviceToken
        }, { withCredentials: true });
      } catch (error) {
        console.error('Failed to clear device trust on server:', error);
      }
    }

    clearDeviceToken();
    logout();

    return { success: true };
  };

  // Clear device token only (keep session if active)
  const clearDeviceToken = () => {
    localStorage.removeItem(DEVICE_KEYS.deviceToken);
    localStorage.removeItem(DEVICE_KEYS.trustedUserInfo);
    setDeviceToken(null);
    setTrustedUserInfo(null);
  };

  // Logout
  const logout = () => {
    unregisterPushToken(api);
    clearAllSessionKeys();
    sessionStorage.removeItem('_mob_impersonating');
    tokenRef.current = null;
    setTokenState(null);
    setOperator(null);
    setAuthType('legacy');
    setTenants([]);
    setPendingPinSetup(false);
    setIsImpersonating(false);
  };

  // End impersonation — clears mobile session and pops the tab back to the SA portal.
  const endImpersonation = () => {
    logout();
    // The SA portal is in another tab; closing this one is the cleanest exit.
    if (typeof window !== 'undefined') {
      try { window.close(); } catch { /* noop */ }
      // Fallback if the browser blocks window.close (e.g. tab opened by user).
      window.location.href = '/super-admin/dashboard';
    }
  };

  // Check if a feature is available based on tenant's plan
  const hasFeature = useCallback((feature) => {
    if (!operator?.tenant_features) return false;
    return operator.tenant_features.includes(feature);
  }, [operator]);

  // Check if operator has mobile quotes access
  const hasMobileQuotesAccess = useCallback(() => {
    if (!operator?.tenant_features?.includes('quotes')) return false;
    // Tenant admins/dispatchers always have quote access on mobile (same as desktop).
    const roles = operator?.roles || [operator?.role];
    if (roles?.some(r => ['admin', 'dispatcher'].includes(r))) return true;
    // Workers need the per-operator mobile quotes flag enabled.
    return operator?.mobile_quotes_enabled === true;
  }, [operator]);

  // Permission check — wraps the shared hasPermission utility using the operator object
  const can = useCallback((permission) => {
    if (!operator) return false;
    return hasPermission(operator, permission);
  }, [operator]);

  // Check if the logged-in user is an admin (wildcard permissions)
  const isAdmin = useCallback(() => {
    if (!operator) return false;
    return isAdminUser(operator);
  }, [operator]);

  // Check if the logged-in user has schedule read access
  const canAccessSchedule = useCallback(() => {
    return can('schedule.read');
  }, [can]);

  // Tenant admin/dispatcher — gate for the mobile "Manage" hub (Basic tier parity)
  // Now permission-based: any non-operator office role
  const isTenantAdmin = useCallback(() => {
    const roles = operator?.roles || [operator?.role];
    return roles?.some(r => ['admin', 'dispatcher', 'accounts', 'compliance_officer', 'viewer', 'payroll'].includes(r));
  }, [operator]);

  // Platform feature flag check (super-admin managed, env/client scoped)
  const isFlagEnabled = useCallback((key) => platformFlags?.[key] === true, [platformFlags]);

  // Check if the logged-in user is a worker (has assigned jobs)
  const isWorker = useCallback(() => {
    // Legacy PIN-login operators are always workers
    if (authType === 'legacy') return true;
    return operator?.is_worker !== false;
  }, [operator, authType]);

  // Timesheet-only licence: worker can only access timesheets + own leave on mobile
  const isTimesheetOnly = useCallback(() => {
    return operator?.mobile_licence_type === 'timesheet';
  }, [operator]);

  const isPPD = useCallback(() => {
    return operator?.mobile_licence_type === 'ppd';
  }, [operator]);

  return (
    <MobileAuthContext.Provider value={{
      // State
      operator,
      loading,
      token,
      authType,      deviceToken,
      trustedUserInfo,
      v2AuthEnabled,
      tenants,
      tenantSettings,
      refreshTenantSettings,
      refreshPlatformFlags,
      pendingPinSetup,
      pendingPasswordChange,

      // API
      api,

      // Legacy auth
      login,

      // V2 auth
      loginV2,
      loginWithPin,
      setupPin,
      switchAccount,
      selectTenant,
      checkDeviceToken,
      clearDeviceToken,
      changePassword,

      // Common
      logout,
      endImpersonation,
      isImpersonating,
      hasFeature,
      hasMobileQuotesAccess,
      canAccessSchedule,
      isTenantAdmin,
      can,
      isAdmin,
      isFlagEnabled,
      platformFlagsLoaded,
      isWorker,
      isTimesheetOnly,
      isPPD,
      subscriptionStatus
    }}>
      {children}
    </MobileAuthContext.Provider>
  );
};

export const useMobileAuth = () => {
  const context = useContext(MobileAuthContext);
  if (!context) {
    throw new Error('useMobileAuth must be used within MobileAuthProvider');
  }
  return context;
};
